import { useCallback, useEffect, useState } from "react";
import { MapPanel, type PlaybackMapFix } from "../components/MapPanel";
import { PlaybackPanel, type PlaybackFix } from "../components/PlaybackPanel";
import { useDevices } from "../hooks/useDevices";
import { useLiveGps } from "../hooks/useLiveGps";
import { useNativeApp } from "../lib/mobile-ui";
import { usePageRefresh } from "../lib/page-lifecycle";
import type { Device } from "../api";

export function PlaybackPage({
  go,
  deviceId,
}: {
  go: (to: string) => void;
  deviceId: string | null;
}) {
  const native = useNativeApp();
  const { devices, loading, error, refresh } = useDevices();
  usePageRefresh(useCallback(() => refresh(), [refresh]));
  const { positions, connected } = useLiveGps();
  const [playbackFix, setPlaybackFix] = useState<PlaybackMapFix | null>(null);

  const selected = devices.find((row) => row.id === deviceId) ?? null;

  useEffect(() => {
    setPlaybackFix(null);
  }, [deviceId]);

  const handlePlaybackFix = useCallback((fix: PlaybackFix | null) => {
    if (!fix) {
      setPlaybackFix((prev) => (prev === null ? prev : null));
      return;
    }
    setPlaybackFix((prev) => {
      const next: PlaybackMapFix = {
        deviceId: fix.deviceId,
        lat: fix.position.lat,
        lon: fix.position.lon,
        heading: fix.position.heading_deg,
        speedKmh: fix.position.speed_kmh,
        recordedAt: fix.recordedAt,
        gap: fix.gap,
        trail: fix.trail,
      };
      if (
        prev &&
        prev.deviceId === next.deviceId &&
        prev.lat === next.lat &&
        prev.lon === next.lon &&
        prev.heading === next.heading &&
        prev.speedKmh === next.speedKmh &&
        prev.recordedAt === next.recordedAt &&
        prev.gap === next.gap &&
        prev.trail === next.trail
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const selectDevice = (id: string) => {
    go(id ? `/playback?device=${encodeURIComponent(id)}` : "/playback");
  };

  return (
    <div className="playback-page">
      <div className="playback-page-toolbar">
        <label>
          Camera
          <select
            value={selected?.id ?? ""}
            onChange={(e) => selectDevice(e.target.value)}
            aria-label="Select camera for playback"
          >
            <option value="">Select a camera</option>
            {devices.map((row: Device) => (
              <option key={row.id} value={row.id}>
                {(row.label || row.serial_no) + (row.online ? "" : " (offline)")}
              </option>
            ))}
          </select>
        </label>
        <span className={`ws ${connected ? "on" : "off"}`}>
          {connected ? "GPS feed connected" : "GPS feed reconnecting..."}
        </span>
      </div>

      {error ? <div className="banner">{error}</div> : null}
      {loading && devices.length === 0 ? <div className="banner">Loading cameras…</div> : null}

      {selected ? (
        <>
          <PlaybackPanel
            key={selected.id}
            device={selected}
            embedMap={native}
            closeLabel="Close"
            onClose={() => go("/")}
            onFix={native ? undefined : handlePlaybackFix}
            onSwitchDevice={selectDevice}
          />
          {!native ? (
            <MapPanel
              devices={devices}
              live={positions}
              selectedId={selected.id}
              onSelect={selectDevice}
              gpsSocketConnected={connected}
              playbackFix={playbackFix}
            />
          ) : null}
        </>
      ) : (
        <p className="empty-grid playback-page-empty">
          Select a camera, then set Start Date, Start Time, End Date, and End Time and click Search to query
          recordings stored on that device.
        </p>
      )}
    </div>
  );
}
